import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, MapPin, LayoutGrid, CalendarDays, Globe, Search, X, Wifi, ListChecks, UserRound, Tag, ArrowUpDown, Shapes, SlidersHorizontal } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import EventsCalendar from '../components/EventsCalendar';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { todRange } from '../lib/datetime';
import { ProgramCard } from '../components/ProgramCard';
import api, { mediaUrl } from '../lib/api';

const TABS = [
  { key: '', label: 'All' },
  { key: 'event', label: 'Events' },
  { key: 'retreat', label: 'Retreats' },
  { key: 'class', label: 'Classes' },
];

const fmtTime = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }); }
  catch { return null; }
};
const sameDay = (a, b) => { try { return new Date(a).toDateString() === new Date(b).toDateString(); } catch { return false; } };

const priceVal = (p) => {
  if (p.is_free) return 0;
  if (p.pricing_type === 'per_day' || p.pricing_type === 'flat_or_per_day') return p.per_day_price || 0;
  if (p.pricing_type === 'tickets') return (p.ticket_types || []).length ? Math.min(...p.ticket_types.map((t) => t.price || 0)) : 0;
  return p.price || 0;
};
const priceLabel = (p) => {
  const cur = p.currency === 'inr' ? '₹' : '$';
  if (p.pricing_type === 'per_day') return `${cur}${p.per_day_price} / day`;
  if (p.pricing_type === 'flat_or_per_day') return `From ${cur}${p.per_day_price} / day`;
  if (p.pricing_type === 'tickets') return (p.ticket_types || []).length ? `From ${cur}${Math.min(...p.ticket_types.map((t) => t.price || 0))}` : 'Tickets';
  return p.is_free ? 'Community-Supported' : `${cur}${p.price}`;
};

const FilterSelect = ({ icon: Icon, value, onChange, children, testid }) => (
  <div className="inline-flex items-center rounded-full bg-white/70 border border-rose/50 pl-3 pr-1 py-1">
    {Icon && <Icon className="w-4 h-4 text-rose-italic mr-1.5" />}
    <select value={value} onChange={onChange} data-testid={testid}
      className="bg-transparent text-sm text-mahogany focus:outline-none pr-1 py-0.5 cursor-pointer max-w-[150px]">{children}</select>
  </div>
);

const Chip = ({ label, onRemove, testid }) => (
  <button onClick={onRemove} data-testid={testid}
    className="inline-flex items-center gap-1 rounded-full bg-rose-soft/60 text-mahogany text-xs px-3 py-1 hover:bg-rose-soft transition-colors">
    {label} <X className="w-3 h-3" />
  </button>
);

const SkeletonCard = () => (
  <div className="bg-white/60 rounded-3xl overflow-hidden border border-rose/30 animate-pulse">
    <div className="aspect-[4/3] bg-rose-soft/40" />
    <div className="p-6 space-y-3">
      <div className="h-3 w-20 bg-rose-soft/50 rounded" />
      <div className="h-5 w-3/4 bg-rose-soft/50 rounded" />
      <div className="h-3 w-full bg-rose-soft/40 rounded" />
      <div className="h-3 w-2/3 bg-rose-soft/40 rounded" />
    </div>
  </div>
);

const AgendaRow = ({ p }) => (
  <Link to={`/programs/${p.slug}`} data-testid={`agenda-row-${p.slug}`}
    className="flex items-center gap-4 bg-white/70 rounded-2xl border border-rose/40 p-3 hover:shadow-md hover:border-rose transition-all">
    <SmartImage src={mediaUrl(p.thumbnail_url || p.featured_image_url)} alt={p.title} className="w-20 h-20 rounded-xl flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="font-serif-display text-lg text-burgundy leading-tight truncate">{p.title}</div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-burgundy/70 mt-1.5">
        {p.start_datetime && fmtTime(p.start_datetime) && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-rose-italic" /> {fmtTime(p.start_datetime)}{p.end_datetime && sameDay(p.start_datetime, p.end_datetime) ? ` – ${fmtTime(p.end_datetime)}` : ''}</span>}
        {p.type === 'class' && p.time_of_day && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-rose-italic" /> {todRange(p.time_of_day, p.duration_minutes)}</span>}
        {(p.location || p.mode === 'online') && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-rose-italic" /> {p.mode === 'online' ? 'Online' : p.location}</span>}
      </div>
    </div>
    <div className="text-sm text-mahogany/80 text-right flex-shrink-0 pr-1">{priceLabel(p)}</div>
  </Link>
);

const AgendaView = ({ items }) => {
  const groups = [];
  const map = {};
  items.forEach((p) => {
    const key = p.start_datetime ? new Date(p.start_datetime).toDateString() : 'Scheduled soon';
    if (!map[key]) { map[key] = []; groups.push(key); }
    map[key].push(p);
  });
  return (
    <div className="mt-10 max-w-3xl mx-auto space-y-8" data-testid="agenda-view">
      {groups.map((g) => (
        <div key={g}>
          <div className="flex items-center gap-3 mb-3">
            <div className="font-serif-display text-xl text-burgundy whitespace-nowrap">{g === 'Scheduled soon' ? g : new Date(g).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</div>
            <div className="flex-1 h-px bg-rose/40" />
          </div>
          <div className="space-y-3">{map[g].map((p) => <AgendaRow key={p.id} p={p} />)}</div>
        </div>
      ))}
    </div>
  );
};

const FORMAT_LABELS = { in_person: 'In person', online: 'Online', hybrid: 'Hybrid' };

const EventsPage = () => {
  const [params] = useSearchParams();
  const typeParam = params.get('type') || '';
  const [tab, setTab] = useState(typeParam);
  // Keep the active type filter in sync with the URL so switching between the
  // "Classes" (/events?type=class) and "Retreats & Events" (/events) nav links
  // updates the list even though it's the same route (no remount).
  useEffect(() => { setTab(typeParam); }, [typeParam]);
  const [view, setView] = useState('list'); // list | calendar | agenda
  const [timeMode, setTimeMode] = useState('upcoming'); // upcoming | past
  const [country, setCountry] = useState('all');
  const [search, setSearch] = useState('');
  const [facilitator, setFacilitator] = useState('all');
  const [month, setMonth] = useState('all');
  const [price, setPrice] = useState('all');
  const [format, setFormat] = useState('all');
  const [sort, setSort] = useState('soonest'); // soonest | price | az
  const [sheetOpen, setSheetOpen] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/programs', { params: { limit: 200 } }).then((r) => r.data || []).catch(() => []),
      api.get('/programs', { params: { past: true, limit: 200 } }).then((r) => r.data || []).catch(() => []),
    ]).then(([up, pa]) => { setUpcoming(up); setPast(pa); }).finally(() => setLoading(false));
  }, []);

  const countryLabel = (p) => p.country || ((p.mode === 'online' || p.mode === 'hybrid') ? 'Online' : null);
  const availableCountries = useMemo(() => {
    const s = new Set();
    [...upcoming, ...past].forEach((p) => { const c = countryLabel(p); if (c) s.add(c); });
    const arr = Array.from(s).filter((c) => c !== 'Online').sort();
    if (s.has('Online')) arr.push('Online');
    return arr;
  }, [upcoming, past]);
  const facilitatorOptions = useMemo(() => {
    const s = new Set();
    [...upcoming, ...past].forEach((p) => (p.facilitator_names || []).forEach((n) => n && s.add(n)));
    return Array.from(s).sort();
  }, [upcoming, past]);
  const monthOptions = useMemo(() => {
    const m = {};
    [...upcoming, ...past].forEach((p) => {
      if (!p.start_datetime) return;
      const d = new Date(p.start_datetime);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      m[key] = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).map(([key, label]) => ({ key, label }));
  }, [upcoming, past]);

  const visible = useMemo(() => {
    const base = timeMode === 'past' ? past : upcoming;
    const cLabel = (p) => p.country || ((p.mode === 'online' || p.mode === 'hybrid') ? 'Online' : null);
    const q = search.trim().toLowerCase();
    let arr = base.filter((p) => {
      if (tab && p.type !== tab) return false;
      if (country !== 'all' && cLabel(p) !== country) return false;
      if (q && !`${p.title || ''} ${p.short_description || ''} ${p.location || ''}`.toLowerCase().includes(q)) return false;
      if (facilitator !== 'all' && !(p.facilitator_names || []).includes(facilitator)) return false;
      if (month !== 'all') {
        if (!p.start_datetime) return false;
        const d = new Date(p.start_datetime);
        if (`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` !== month) return false;
      }
      if (price === 'free' && !p.is_free) return false;
      if (price === 'paid' && p.is_free) return false;
      if (format !== 'all' && p.mode !== format) return false;
      return true;
    });
    arr = [...arr].sort((a, b) => {
      if (sort === 'az') return (a.title || '').localeCompare(b.title || '');
      if (sort === 'price') return priceVal(a) - priceVal(b);
      const ta = a.start_datetime ? new Date(a.start_datetime).getTime() : Infinity;
      const tb = b.start_datetime ? new Date(b.start_datetime).getTime() : Infinity;
      return ta - tb;
    });
    return arr;
  }, [upcoming, past, timeMode, tab, country, search, facilitator, month, price, format, sort]);

  const chips = [];
  if (tab) chips.push({ key: 't', label: TABS.find((t) => t.key === tab)?.label || tab, clear: () => setTab('') });
  if (search.trim()) chips.push({ key: 's', label: `“${search.trim()}”`, clear: () => setSearch('') });
  if (country !== 'all') chips.push({ key: 'c', label: country, clear: () => setCountry('all') });
  if (facilitator !== 'all') chips.push({ key: 'f', label: facilitator, clear: () => setFacilitator('all') });
  if (month !== 'all') chips.push({ key: 'm', label: monthOptions.find((o) => o.key === month)?.label || month, clear: () => setMonth('all') });
  if (price !== 'all') chips.push({ key: 'p', label: price === 'free' ? 'Free' : 'Paid', clear: () => setPrice('all') });
  if (format !== 'all') chips.push({ key: 'fmt', label: FORMAT_LABELS[format], clear: () => setFormat('all') });
  const clearAll = () => { setTab(''); setSearch(''); setCountry('all'); setFacilitator('all'); setMonth('all'); setPrice('all'); setFormat('all'); };

  const viewBtn = (key, Icon, label) => (
    <button onClick={() => setView(key)} data-testid={`view-${key}`} title={label} aria-label={label}
      className={`px-3 sm:px-3.5 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 transition-colors ${view === key ? 'bg-rose-soft text-burgundy shadow-sm' : 'text-mahogany/60 hover:text-mahogany'}`}>
      <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{label}</span>
    </button>
  );

  const timeToggle = (
    <div className="inline-flex rounded-full bg-white/70 border border-rose/50 p-1">
      <button onClick={() => setTimeMode('upcoming')} data-testid="time-upcoming"
        className={`px-4 py-1.5 rounded-full text-sm transition-colors ${timeMode === 'upcoming' ? 'bg-burgundy text-cream' : 'text-mahogany/70'}`}>Upcoming</button>
      <button onClick={() => setTimeMode('past')} data-testid="time-past"
        className={`px-4 py-1.5 rounded-full text-sm transition-colors ${timeMode === 'past' ? 'bg-burgundy text-cream' : 'text-mahogany/70'}`}>Past</button>
    </div>
  );

  const filterControls = (
    <>
      <FilterSelect icon={Shapes} value={tab} onChange={(e) => setTab(e.target.value)} testid="type-filter">
        {TABS.map((t) => <option key={t.key} value={t.key}>{t.key ? t.label : 'All types'}</option>)}
      </FilterSelect>
      {availableCountries.length > 0 && (
        <FilterSelect icon={Globe} value={country} onChange={(e) => setCountry(e.target.value)} testid="country-filter">
          <option value="all">All countries</option>
          {availableCountries.map((c) => <option key={c} value={c}>{c === 'Online' ? '🌐 Online' : c}</option>)}
        </FilterSelect>
      )}
      {facilitatorOptions.length > 0 && (
        <FilterSelect icon={UserRound} value={facilitator} onChange={(e) => setFacilitator(e.target.value)} testid="facilitator-filter">
          <option value="all">All facilitators</option>
          {facilitatorOptions.map((f) => <option key={f} value={f}>{f}</option>)}
        </FilterSelect>
      )}
      {monthOptions.length > 0 && (
        <FilterSelect icon={Calendar} value={month} onChange={(e) => setMonth(e.target.value)} testid="month-filter">
          <option value="all">Any month</option>
          {monthOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </FilterSelect>
      )}
      <FilterSelect icon={Tag} value={price} onChange={(e) => setPrice(e.target.value)} testid="price-filter">
        <option value="all">Any price</option>
        <option value="free">Free</option>
        <option value="paid">Paid</option>
      </FilterSelect>
      <FilterSelect icon={Wifi} value={format} onChange={(e) => setFormat(e.target.value)} testid="format-filter">
        <option value="all">Any format</option>
        <option value="in_person">In person</option>
        <option value="online">Online</option>
        <option value="hybrid">Hybrid</option>
      </FilterSelect>
      <FilterSelect icon={ArrowUpDown} value={sort} onChange={(e) => setSort(e.target.value)} testid="sort-filter">
        <option value="soonest">Soonest first</option>
        <option value="price">Price: low to high</option>
        <option value="az">A – Z</option>
      </FilterSelect>
    </>
  );

  return (
    <div className="App">
      <Header />
      <main className="bg-cream min-h-[60vh]">
        <section className="max-w-[1300px] mx-auto px-6 lg:px-10 py-16">
          <div className="text-center">
            <div className="eyebrow mb-3">What&apos;s On</div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-burgundy">Events, Retreats &amp; <span className="font-italic-display text-rose-italic">Classes</span></h1>
          </div>

          {/* Toolbar */}
          <div className="mt-8 space-y-4">
            {/* Row A: view toggle */}
            <div className="flex flex-wrap items-center justify-end gap-3">
              <div className="inline-flex rounded-full bg-white/70 border border-rose/50 p-1" data-testid="view-toggle">
                {viewBtn('list', LayoutGrid, 'List')}
                {viewBtn('calendar', CalendarDays, 'Calendar')}
                {viewBtn('agenda', ListChecks, 'Agenda')}
              </div>
            </div>

            {/* Row B: search (always) + time/filters (desktop inline, mobile in sheet) */}
            <div className="flex items-center gap-2.5">
              <div className="inline-flex items-center rounded-full bg-white/70 border border-rose/50 px-3 py-1.5 flex-1 min-w-0 md:max-w-xs">
                <Search className="w-4 h-4 text-rose-italic mr-2 flex-shrink-0" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search events…" data-testid="events-search"
                  className="bg-transparent text-sm text-burgundy placeholder-mahogany/40 focus:outline-none w-full" />
                {search && <button onClick={() => setSearch('')} data-testid="events-search-clear"><X className="w-4 h-4 text-mahogany/50" /></button>}
              </div>
              {/* Mobile: open filter sheet */}
              <button onClick={() => setSheetOpen(true)} data-testid="open-filters"
                className="md:hidden inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-rose/50 px-4 py-1.5 text-sm text-mahogany flex-shrink-0">
                <SlidersHorizontal className="w-4 h-4 text-rose-italic" /> Filters
                {chips.length > 0 && <span className="ml-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-burgundy text-cream text-[11px]" data-testid="filters-badge">{chips.length}</span>}
              </button>
            </div>

            {/* Desktop inline filters */}
            <div className="hidden md:flex flex-wrap items-center gap-2.5">
              {timeToggle}
              {filterControls}
            </div>

            {/* Mobile filter sheet */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetContent side="bottom" className="bg-cream border-rose/40 rounded-t-3xl max-h-[85vh] overflow-y-auto" data-testid="filters-sheet">
                <SheetHeader className="text-left">
                  <SheetTitle className="font-serif-display text-burgundy text-xl">Filter events</SheetTitle>
                </SheetHeader>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {timeToggle}
                  {filterControls}
                </div>
                <div className="mt-6 flex items-center gap-3">
                  <button onClick={clearAll} data-testid="sheet-clear" className="text-sm text-rose-italic underline underline-offset-2">Clear all</button>
                  <button onClick={() => setSheetOpen(false)} data-testid="sheet-apply"
                    className="flex-1 rounded-full bg-mahogany text-cream py-2.5 text-sm font-medium">Show {visible.length} event{visible.length === 1 ? '' : 's'}</button>
                </div>
              </SheetContent>
            </Sheet>

            {/* Row C: result count + chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-mahogany/70" data-testid="events-count">{loading ? 'Loading…' : `${visible.length} event${visible.length === 1 ? '' : 's'}`}</span>
              {chips.map((c) => <Chip key={c.key} label={c.label} onRemove={c.clear} testid={`chip-${c.key}`} />)}
              {chips.length > 0 && <button onClick={clearAll} data-testid="clear-filters" className="text-xs text-rose-italic hover:text-burgundy underline underline-offset-2">Clear all</button>}
            </div>
          </div>

          {loading ? (
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : view === 'calendar' ? (
            <div className="mt-10"><EventsCalendar items={visible} /></div>
          ) : visible.length === 0 ? (
            <div className="mt-16 text-center text-mahogany/60" data-testid="events-empty">
              {chips.length > 0 ? (
                <>No events match your filters. <button onClick={clearAll} className="text-rose-italic underline">Clear all</button></>
              ) : timeMode === 'past' ? 'No past events to show yet.' : 'No upcoming programs. Please check back soon.'}
            </div>
          ) : view === 'agenda' ? (
            <AgendaView items={visible} />
          ) : (
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {visible.map((p) => <ProgramCard key={p.id} p={p} />)}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default EventsPage;
